import { Box, Center, Flex, HStack, Image, Text } from '@chakra-ui/react';
import Spinner from '../../assets/images/spinner.png';
import { FC, useEffect, useState, useRef } from 'react';
import { CloseIcon } from '@chakra-ui/icons';
import './voice-chat.styles.css';
import { useQueryClient } from '@tanstack/react-query';
import VoiceWaveForm from './VoiceWaveForm';
import { sendAudioBuffer } from '../../clients/be';
import { InputMic } from './inputMic';
import { useSpeaking } from '../../stores/speaking';
import { useTranslation } from 'react-i18next';
import BackButton from '../common/BackButton';
import AdelIcon from '../../assets/icons/talk-to-adel.png';
import NoorIcon from '../../assets/icons/talk-to-noor.png';
import RecordIcon from '../../assets/icons/new-record-icon.png';

const VoiceChatWindow: FC<{
  onClose: () => void;
  conversationId: string;
  selectedVoice: string;
  isArabic: boolean;
}> = ({ conversationId, selectedVoice, isArabic, onClose }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [loadingMessage, setLoadingMessage] = useState('researching');

  const { isSpeaking } = useSpeaking();

  const [sourceUrl, setSourceUrl] = useState<string>('');

  // const [isConnecting, setIsConnecting] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);

  const audioResponseRef = useRef<HTMLAudioElement | null>(null);

  async function sendAudio(audioBlob: Blob) {
    setIsProcessing(true);
    try {
      const audioUrl = await sendAudioBuffer(
        conversationId,
        selectedVoice,
        audioBlob
      );
      setSourceUrl(audioUrl);
      queryClient.invalidateQueries({
        queryKey: ['conversation', conversationId],
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsProcessing(false);
    }
  }

  useEffect(() => {
    let intervalId: any; // Store the interval ID
    if (isProcessing) {
      intervalId = setInterval(() => {
        switch (loadingMessage) {
          case 'reviewing':
            setLoadingMessage('researching');
            break;
          default:
            setLoadingMessage('researching');
        }
      }, 2000);
    }

    return () => {
      // Cleanup function for the useEffect
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isProcessing, loadingMessage]);

  // useEffect(() => {
  //   let timeoutId: ReturnType<typeof setTimeout> | null = null;

  //   setIsConnecting(true);
  //   timeoutId = setTimeout(() => {
  //     setIsConnecting(false);
  //   }, 1500);

  //   return () => {
  //     if (timeoutId) {
  //       clearTimeout(timeoutId);
  //     }
  //   };
  // }, []);

  useEffect(() => {
    if (!sourceUrl) return;

    if (!audioResponseRef.current) return;

    audioResponseRef.current.src = sourceUrl;
    audioResponseRef.current.load();
  }, [sourceUrl]);

  useEffect(() => {
    if (!audioResponseRef.current) return;

    audioResponseRef.current.addEventListener('ended', () => {
      setIsAudioPlaying(false);
    });

    audioResponseRef.current.addEventListener('canplaythrough', () => {
      if (!audioResponseRef.current) return;
      audioResponseRef.current.play();
    });

    audioResponseRef.current.addEventListener('play', () => {
      setIsAudioPlaying(true);
    });
  }, [audioResponseRef.current]);

  const onCloseVoiceChat = () => {
    if (audioResponseRef.current) {
      audioResponseRef.current.pause();
    }
    onClose();
  };

  return (
    <Flex
      w="full"
      bg="white"
      zIndex={5}
      pos="absolute"
      top="0"
      bottom="0"
      right="0"
      flexDir="column"
      h="100%"
      overflow="hidden"
      alignItems="space-between"
    >
      <audio ref={audioResponseRef} />

      <Flex
        w="full"
        h="3rem"
        alignItems="center"
        justifyContent="flex-end"
        pe={10}
        pt={10}
      >
        <BackButton
          onClick={onCloseVoiceChat}
          btnText={t('backToChat')}
          isArabic={isArabic}
        />
      </Flex>

      <Flex
        flexDir="column"
        justifyContent="space-between"
        w="full"
        height="full"
        pt="3"
        pb="0"
      >
        {/* Banner text */}
        <Flex
          pos="absolute"
          top={{ base: '32%', md: '28%', '2xl': '30%' }}
          w="100%"
          transform="translateY(-40%)"
          alignItems="center"
          justify="center"
          px="1rem"
          flexDir="column"
        >
          <Center w="full" flexDir="column">
            <Flex gap=".3rem" mt="2">
              <Text fontWeight="semibold" fontSize={{ base: 'lg', md: '2xl' }}>
                {t('talkTo')}
              </Text>
              <Text
                fontFamily="Blenda Script"
                fontSize={{ base: 'lg', md: '2xl' }}
              >
                {selectedVoice === '3' ? t('adel') : t('noor')}
              </Text>
            </Flex>
            <Text
              fontSize={{ base: '0.8rem', md: '1.1rem' }}
              color="#6B7584"
              mb="2rem"
              textAlign="center"
            >
              {t('adelIntro')}
            </Text>
          </Center>
          {selectedVoice === '3' ? (
            <>
              <Image src={AdelIcon} h={{ base: '9rem', md: '13rem' }} />
              <Center h="3rem">
                {isProcessing ? (
                  <Text>{t(loadingMessage)}</Text>
                ) : isAudioPlaying ? (
                  <Text>{t('adelTalking')}</Text>
                ) : isSpeaking ? (
                  <Text>{t('adelListening')}</Text>
                ) : null}
              </Center>
            </>
          ) : (
            <>
              <Image src={NoorIcon} h="13rem" />
              <Center h="3rem">
                {isProcessing ? (
                  <Text>{t(loadingMessage)}</Text>
                ) : isAudioPlaying ? (
                  <Text>{t('noorTalking')}</Text>
                ) : isSpeaking ? (
                  <Text>{t('noorListening')}</Text>
                ) : null}
              </Center>
            </>
          )}
        </Flex>
      </Flex>

      <Flex
        gap={5}
        w="full"
        h="full"
        direction="column"
        alignItems="center"
        justifyContent="center"
      >
        {/* {isConnecting && (
          <>
            <Image src={Spinner} alt="spinner" className="spinner" />
            <Text textAlign="center" color="white">
              {t('connecting')}
            </Text>
          </>
        )} */}

        {isAudioPlaying && (
          <button className="w-fit h-fit opacity-50">
            <Text fontWeight="bold" fontSize="lg" mb="3">
              {t('tapToTalk')}
            </Text>
            <Image src={RecordIcon} h={{ base: '5rem', md: '7rem' }} />
          </button>
        )}
        {!isAudioPlaying && (
          <InputMic onDataAvailable={sendAudio} isDisbaled={isProcessing} />
        )}
      </Flex>
    </Flex>
  );
};

export default VoiceChatWindow;
